/**
 * FUMO Waitlist Drawer
 * Pre-launch email capture panel with product context support.
 */
(function () {
  'use strict';

  /* ======================================================================
     1. References & State
     ====================================================================== */
  var drawer, backdrop, panel, closeBtn, form, emailInput, errorEl, submitBtn;
  var formState, successState, productEl, productImage, productName, tagsInput;
  var isOpen = false;
  var triggerElement = null;
  var savedScrollY = 0;
  var currentProduct = null;

  /* ======================================================================
     2. Open / Close
     ====================================================================== */
  function openDrawer(productData) {
    if (isOpen) return;
    isOpen = true;

    triggerElement = document.activeElement;
    currentProduct = productData || null;

    // Set product context
    setProductContext(currentProduct);

    // Update tags with product handle
    updateTags(currentProduct);

    // Scroll lock
    savedScrollY = window.pageYOffset;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.width = '100%';

    // Inert on background content
    setBackgroundInert(true);

    // Show
    backdrop.classList.add('fumo-waitlist-drawer__backdrop--visible');
    panel.classList.remove('fumo-waitlist-drawer__panel--closing');
    panel.classList.add('fumo-waitlist-drawer__panel--open');
    drawer.setAttribute('aria-hidden', 'false');

    // Reset to form state
    if (formState) formState.style.display = '';
    if (successState) successState.style.display = 'none';
    if (submitBtn) submitBtn.disabled = false;
    if (emailInput) emailInput.value = '';
    if (errorEl) errorEl.setAttribute('data-visible', 'false');

    // Focus email input
    requestAnimationFrame(function () {
      if (emailInput) emailInput.focus();
    });
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;

    backdrop.classList.remove('fumo-waitlist-drawer__backdrop--visible');
    panel.classList.add('fumo-waitlist-drawer__panel--closing');
    panel.classList.remove('fumo-waitlist-drawer__panel--open');
    drawer.setAttribute('aria-hidden', 'true');

    // Restore scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);

    // Remove inert
    setBackgroundInert(false);

    // Return focus
    if (triggerElement && triggerElement.focus) {
      triggerElement.focus();
    }
  }

  /* ======================================================================
     3. Product Context
     ====================================================================== */
  function setProductContext(data) {
    if (!productEl) return;

    if (data && data.name) {
      if (productImage) {
        productImage.src = data.image || '';
        productImage.alt = data.name;
      }
      if (productName) productName.textContent = data.name;
      productEl.style.display = '';
    } else {
      productEl.style.display = 'none';
    }
  }

  function updateTags(data) {
    if (!tagsInput) return;
    var baseTags = 'stillness-list,founders-circle,cart-intent,prospect';
    if (data && data.handle) {
      baseTags += ',' + data.handle;
    }
    tagsInput.value = baseTags;
  }

  /* ======================================================================
     4. Inert Helper
     ====================================================================== */
  function setBackgroundInert(inert) {
    var selectors = ['#MainContent', '#header-group', '#shopify-section-footer'];
    selectors.forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el) return;
      if (inert) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    });

    var footerGroup = document.querySelector('[id*="footer-group"]');
    if (footerGroup) {
      if (inert) {
        footerGroup.setAttribute('inert', '');
      } else {
        footerGroup.removeAttribute('inert');
      }
    }
  }

  /* ======================================================================
     5. Focus Trap
     ====================================================================== */
  function handleFocusTrap(e) {
    if (!isOpen || e.key !== 'Tab') return;

    var focusable = panel.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ======================================================================
     6. Form Submission
     ====================================================================== */
  function handleSubmit(e) {
    e.preventDefault();

    // Honeypot check
    var hp = form.querySelector('[data-fumo-hp]');
    if (hp && hp.value) return;

    var email = emailInput ? emailInput.value.trim() : '';

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (errorEl) errorEl.setAttribute('data-visible', 'true');
      if (emailInput) emailInput.focus();
      return;
    }

    if (errorEl) errorEl.setAttribute('data-visible', 'false');
    var originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending...';
    }

    var formData = new FormData(form);

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    })
      .then(function () {
        // Klaviyo identify
        var handle = currentProduct && currentProduct.handle ? currentProduct.handle : 'all';
        window._learnq = window._learnq || [];
        window._learnq.push([
          'identify',
          {
            $email: email,
            signup_source: 'cart-drawer',
            product_interest: handle,
          },
        ]);

        // Show success
        if (formState) formState.style.display = 'none';
        if (successState) successState.style.display = '';
      })
      .catch(function () {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
        if (errorEl) {
          errorEl.textContent = 'Something went wrong. Please try again.';
          errorEl.setAttribute('data-visible', 'true');
        }
        if (emailInput) {
          emailInput.addEventListener('focus', function clearErr() {
            if (errorEl) errorEl.setAttribute('data-visible', 'false');
            emailInput.removeEventListener('focus', clearErr);
          });
        }
      });
  }

  /* ======================================================================
     7. Bind Triggers
     ====================================================================== */
  function bindTriggers() {
    // Explicit waitlist trigger buttons
    document.querySelectorAll('[data-waitlist-trigger]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        var data = null;
        var handle = el.getAttribute('data-product-handle');
        if (handle) {
          data = {
            handle: handle,
            name: el.getAttribute('data-product-name') || '',
            image: el.getAttribute('data-product-image') || '',
          };
        }
        openDrawer(data);
      });
    });

    // Intercept all cart links (same pattern as fumo-cart-drawer.js enhanceCartIcon)
    document.querySelectorAll('a[href="/cart"]').forEach(function (link) {
      if (link.closest('.fumo-waitlist-drawer')) return;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openDrawer(null);
      });
    });
  }

  /* ======================================================================
     8. Event Listeners
     ====================================================================== */
  function bindGlobalEvents() {
    // Custom event for programmatic opens
    document.addEventListener('fumo:waitlist:open', function (e) {
      openDrawer(e.detail || null);
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) {
        closeDrawer();
      }
      handleFocusTrap(e);
    });
  }

  /* ======================================================================
     9. Init
     ====================================================================== */
  function init() {
    drawer = document.querySelector('.fumo-waitlist-drawer');
    if (!drawer) return;

    backdrop = drawer.querySelector('.fumo-waitlist-drawer__backdrop');
    panel = drawer.querySelector('.fumo-waitlist-drawer__panel');
    closeBtn = drawer.querySelector('.fumo-waitlist-drawer__close');
    form = drawer.querySelector('.fumo-waitlist-drawer__form');
    emailInput = drawer.querySelector('[data-fumo-email]');
    errorEl = drawer.querySelector('.fumo-waitlist-drawer__error');
    submitBtn = drawer.querySelector('.fumo-waitlist-drawer__btn');
    formState = drawer.querySelector('[data-waitlist-form-state]');
    successState = drawer.querySelector('[data-waitlist-success]');
    productEl = drawer.querySelector('[data-waitlist-product]');
    productImage = drawer.querySelector('[data-waitlist-product-image]');
    productName = drawer.querySelector('[data-waitlist-product-name]');
    tagsInput = drawer.querySelector('[data-waitlist-tags]');

    if (backdrop) {
      backdrop.addEventListener('click', closeDrawer);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }

    if (form) {
      form.addEventListener('submit', handleSubmit);
    }

    bindTriggers();
    bindGlobalEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
